import { parseResponse } from "helpers/common.lib";
import { baseApi } from "../../redux/baseAPI";

/**
 * RTK Query service for the Wound Assessment module.
 *
 * Confirmed endpoints:
 *   - GET  /woundcare/index.json   -> listing
 *
 * The remaining CRUD endpoints below follow the standard
 * "/<resource>/<action>.json" convention used elsewhere in this codebase
 * (see fbService, familyIncomeService, advanceDirectiveService). If the
 * backend uses different paths, only the `url` strings need to change here.
 */
export const woundCareApi = baseApi.injectEndpoints({
  tagTypes: ["WoundCareList", "WoundCareItem", "WoundCareReport"],
  endpoints: (builder) => ({

    // -----------------------------------------------------------------
    // Listing
    // -----------------------------------------------------------------
    // Caller passes any combination of identifiers the backend supports,
    // typically: { pid, encounter_id, note_id }. The query string is
    // forwarded as-is so the service stays generic.
    getWoundCareList: builder.query({
      query: (queryParams = {}) => ({
        url: `/woundcare/index.json`,
        method: "GET",
        params: queryParams,
      }),
      transformResponse: (response) => {
        const { data } = parseResponse(response);
        return data;
      },
      providesTags: (result) => {
        const list = Array.isArray(result)
          ? result
          : Array.isArray(result?.wounds)
            ? result.wounds
            : [];
        return list.length
          ? [
              ...list.map((w) => ({ type: "WoundCareItem", id: w?.id })),
              { type: "WoundCareList", id: "LIST" },
            ]
          : [{ type: "WoundCareList", id: "LIST" }];
      },
    }),

    // -----------------------------------------------------------------
    // Single wound detail
    // -----------------------------------------------------------------
    getWoundCare: builder.query({
      query: (queryParams = {}) => ({
        url: `/PADocumate/esync_note_progress.json`,
        method: "GET",
        params: queryParams,
      }),
      transformResponse: (response) => {
        const { data } = parseResponse(response);
        return data;
      },
      providesTags: (result, error, arg) => [
        { type: "WoundCareItem", id: arg?.id || "DETAIL" },
      ],
    }),

    // -----------------------------------------------------------------
    // Create / Update / Delete a single wound
    // -----------------------------------------------------------------
    saveEncounterNoteWoundCare: builder.mutation({
      query: (body) => ({
        url: `/woundcare/edit.json`,
        method: "POST",
        data: body,
      }),
      transformResponse: (response) => parseResponse(response),
      invalidatesTags: [{ type: "WoundCareList", id: "LIST" }],
    }),

    // -----------------------------------------------------------------
    // Delete a single wound-care record
    // -----------------------------------------------------------------
    // The caller passes the identifiers the backend needs (typically
    // `{ id, pid, encounter }`). The body is forwarded as-is so this
    // mutation stays generic.
    deleteWoundCare: builder.mutation({
      query: (body) => ({
        url: `/woundcare/delete.json`,
        method: "POST",
        data: body,
      }),
      transformResponse: (response) => parseResponse(response),
      invalidatesTags: (result, error, arg) => [
        { type: "WoundCareList", id: "LIST" },
        { type: "WoundCareItem", id: arg?.id || "DETAIL" },
      ],
    }),

    // -----------------------------------------------------------------
    // Persist a full report at once (wounds + overall summary + barriers).
    // Useful when the user clicks "Save" on the listing dialog and we want
    // to commit everything in a single round-trip.
    // -----------------------------------------------------------------
    saveWoundReport: builder.mutation({
      query: (body) => ({
        url: `/PADocumate/update_thread.json`,
        method: "POST",
        data: body,
      }),
      transformResponse: (response) => parseResponse(response),
      invalidatesTags: [
        { type: "WoundCareList", id: "LIST" },
        { type: "WoundCareReport", id: "REPORT" },
      ],
    }),

    // -----------------------------------------------------------------
    // Lightweight "fetch the saved report" companion (overall summary +
    // barriers + any report-level meta the backend wants to return).
    // -----------------------------------------------------------------
    getWoundReport: builder.query({
      query: (queryParams = {}) => ({
        url: `/woundcare/report.json`,
        method: "GET",
        params: queryParams,
      }),
      transformResponse: (response) => {
        const { data } = parseResponse(response);
        return data;
      },
      providesTags: [{ type: "WoundCareReport", id: "REPORT" }],
    }),

    // -----------------------------------------------------------------
    // Wound-care reference metadata (dropdown options).
    // GET /woundcare/meta.json — returns lists like wound_etiology,
    // stage_severity, exudate_amount, periwound, primary_dressing, etc.
    // The caller may pass identifiers the backend expects (typically pid);
    // the params are forwarded as-is.
    // -----------------------------------------------------------------
    getWoundCareMeta: builder.query({
      query: (queryParams = {}) => ({
        url: `/woundcare/meta.json`,
        method: "GET",
        params: queryParams,
      }),
      transformResponse: (response) => {
        const { data } = parseResponse(response);
        return data || {};
      },
    }),
  }),
});

export const {
  useGetWoundCareListQuery,
  useGetWoundCareQuery,
  useSaveEncounterNoteWoundCareMutation,
  useDeleteWoundCareMutation,
  useSaveWoundReportMutation,
  useGetWoundReportQuery,
  useGetWoundCareMetaQuery,
} = woundCareApi;
